'use client'

import { useState, useEffect } from 'react'
import { ClinicaService, ClinicaItem, EstadoItem, CidadeItem, BairroItem } from '../../../lib/services'
import HorariosClinica from '../../components/HorariosClinica'
import { SearchableSelect } from '../../components/ClinicaSearch'
import { formatCEP, cleanCEP } from '../../../lib/utils/formatters'
import LocationPromptModal from '../../components/LocationPromptModal'
import CompanyFormModal from '../../components/CompanyFormModal'

// A interface Clinica agora vem dos tipos compartilhados
// mas vamos manter uma interface local para compatibilidade

const slugify = (str: string) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

async function fetchNominatimCoordinates(query: string): Promise<{ lat: number, lng: number } | null> {
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`;
    const response = await fetch(url, { headers: { 'Accept-Language': 'pt-BR' } });
    const data = await response.json();
    if (data && data.length > 0) {
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    }
  } catch (error) {
    console.error('Nominatim geocode error:', error);
  }
  return null;
}

export default function ClinicasClient({ initialSlug = [] }: { initialSlug?: string[] }) {
  const [searchCep, setSearchCep] = useState('')
  const [selectedUf, setSelectedUf] = useState(initialSlug[0] ? decodeURIComponent(initialSlug[0]).toUpperCase() : '')
  const [selectedCidade, setSelectedCidade] = useState('')
  const [selectedBairro, setSelectedBairro] = useState('')
  const [processedInitials, setProcessedInitials] = useState({
    cidade: !initialSlug[1],
    bairro: !initialSlug[2],
  })

  const [clinicas, setClinicas] = useState<ClinicaItem[]>([])
  const [filteredClinicas, setFilteredClinicas] = useState<ClinicaItem[]>([])
  const [nearbyMessage, setNearbyMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [showCompanyFormModal, setShowCompanyFormModal] = useState(false)
  const [hasPromptedLocation, setHasPromptedLocation] = useState(false)
  const [loadingCidades, setLoadingCidades] = useState(initialSlug[0] ? true : false)
  const [loadingBairros, setLoadingBairros] = useState(initialSlug[1] ? true : false)
  const [cepInfo, setCepInfo] = useState<any>(null)
  const [clinicaHorarios, setClinicaHorarios] = useState<ClinicaItem | null>(null)
  const [estados, setEstados] = useState<EstadoItem[]>([])
  const [cidades, setCidades] = useState<CidadeItem[]>([])
  const [bairros, setBairros] = useState<BairroItem[]>([])

  // Carregar estados disponíveis
  useEffect(() => {
    const loadEstados = async () => {
      try {
        const estadosData = await ClinicaService.getEstados()
        // Transformar para o formato esperado
        const estadosFormatted = estadosData.map((estado: any) => ({
          uf: estado.uf,
          nome: estado.nome
        }))
        setEstados(estadosFormatted)
      } catch (error) {
        console.error('Erro ao carregar estados:', error)
        // Usar estados mockados como fallback
        setEstados(estadosMock)
      }
    }

    loadEstados()
  }, [])

  // Carregar cidades
  useEffect(() => {
    if (!selectedUf) {
      setCidades([])
      setSelectedCidade('')
      return
    }
    const loadCidades = async () => {
      setLoadingCidades(true)
      try {
        const cidadesData = await ClinicaService.getCidadesByEstado(selectedUf)
        setCidades(cidadesData)
        
        // Resolve initial slug for cidade
        if (initialSlug[1] && !processedInitials.cidade) {
          const slug1 = slugify(decodeURIComponent(initialSlug[1]))
          const match = cidadesData.find((c: any) => slugify(c.cidade) === slug1)
          if (match) {
            setSelectedCidade((match as any).cidade)
          } else {
            setSelectedCidade(decodeURIComponent(initialSlug[1]))
          }
          setProcessedInitials(prev => ({ ...prev, cidade: true }))
        }
      } catch (error) {
        console.error('Erro ao carregar cidades:', error)
        setProcessedInitials(prev => ({ ...prev, cidade: true }))
      } finally {
        setLoadingCidades(false)
      }
    }
    loadCidades()
  }, [selectedUf, initialSlug, processedInitials.cidade])

  // Carregar bairros
  useEffect(() => {
    if (!selectedUf || !selectedCidade) {
      setBairros([])
      setSelectedBairro('')
      return
    }
    const loadBairros = async () => {
      setLoadingBairros(true)
      try {
        const bairrosData = await ClinicaService.getBairrosByCidadeEstado(selectedUf, selectedCidade)
        setBairros(bairrosData)
        
        // Resolve initial slug for bairro
        if (initialSlug[2] && !processedInitials.bairro) {
          const slug2 = slugify(decodeURIComponent(initialSlug[2]))
          const match = bairrosData.find((b: any) => slugify(b.nome || b.bairro) === slug2)
          if (match) {
            setSelectedBairro((match as any).nome || (match as any).bairro)
          } else {
            setSelectedBairro(decodeURIComponent(initialSlug[2]))
          }
          setProcessedInitials(prev => ({ ...prev, bairro: true }))
        }
      } catch (error) {
        console.error('Erro ao carregar bairros:', error)
        setProcessedInitials(prev => ({ ...prev, bairro: true }))
      } finally {
        setLoadingBairros(false)
      }
    }
    // Dont fetch if cidade is not processed to avoid loading with fallback 'sao-paulo' instead of 'São Paulo'
    if (processedInitials.cidade) {
      loadBairros()
    }
  }, [selectedUf, selectedCidade, initialSlug, processedInitials.cidade, processedInitials.bairro])

  // Carregar clínicas quando filtros mudarem
  useEffect(() => {
    // Wait until initial slugs are processed
    if (initialSlug[1] && !processedInitials.cidade) return;
    if (initialSlug[2] && !processedInitials.bairro) return;

    const loadClinicas = async () => {
      setLoading(true)
      try {
        let clinicasData: any[] = []
        setNearbyMessage('')

        if (selectedUf || selectedCidade || selectedBairro) {
          // Attempt 1: Exact Match
          clinicasData = await ClinicaService.getClinicasFiltradas({
            estado: selectedUf || undefined,
            cidade: selectedCidade || undefined,
            bairro: selectedBairro || undefined,
          })
          
          // Fallback geográfico se vier vazia
          if (clinicasData.length === 0) {
             const queryTerms = [];
             if (selectedBairro) queryTerms.push(selectedBairro);
             if (selectedCidade) queryTerms.push(selectedCidade);
             if (selectedUf) queryTerms.push(selectedUf);
             queryTerms.push('Brasil');
             
             const queryLocation = queryTerms.join(', ');
             const coords = await fetchNominatimCoordinates(queryLocation);
             
             if (coords) {
                clinicasData = await ClinicaService.getClinicasFiltradas({
                  lat: coords.lat,
                  lng: coords.lng,
                });
                
                if (clinicasData.length > 0) {
                   setNearbyMessage(`Não encontramos clínicas correspondentes ao termo exato, mas resgatamos as opções mais próximas na região.`);
                }
             } else if (selectedCidade && selectedUf) {
                // Último caso se nominatim falhar (fallback tradicional)
                clinicasData = await ClinicaService.getClinicasFiltradas({
                  estado: selectedUf,
                  cidade: selectedCidade,
                });
                if (clinicasData.length > 0) {
                   setNearbyMessage(`Não encontramos clínicas no bairro, listamos opções na mesma cidade (${selectedCidade}).`);
                }
             }
          }
        } else {
          // Sem filtros: Mostrar modal customizado de geolocalização se ainda não avaliado
          if (!hasPromptedLocation) {
             setLoading(false)
             setShowLocationModal(true)
             return // Trava o fluxo da refatoração
          } else {
             clinicasData = await ClinicaService.getClinicas()
          }
        }

        // Os dados já vêm no formato correto da API
        const clinicasFormatted: (ClinicaItem & { distanciaKm?: number })[] = clinicasData.map((clinica: any) => ({
          id: clinica.id,
          nome: clinica.nome,
          bairro: clinica.bairro || '',
          cidade: clinica.cidade,
          uf: clinica.uf,
          cep: clinica.cep,
          endereco: clinica.endereco,
          numero: clinica.numero,
          latitude: clinica.latitude,
          longitude: clinica.longitude,
          geolocalizacao_atualizada_em: clinica.geolocalizacao_atualizada_em,
          distanciaKm: clinica.distanciaKm,
          horarios: clinica.horarios || []
        }))

        setClinicas(clinicasFormatted)
        setFilteredClinicas(clinicasFormatted)
        setCepInfo(null) // Limpar info de CEP quando filtros mudam

        // Background Job Client-Side: Rehidratação de Clínicas defasadas
        const dehydrated = clinicasFormatted.filter((c: any) => !c.geolocalizacao_atualizada_em);
        if (dehydrated.length > 0) {
          dehydrated.forEach(async (clinica: any, i: number) => {
             try {
                await new Promise(r => setTimeout(r, i * 1500));
                const params = new URLSearchParams();
                if (clinica.endereco) {
                    const street = clinica.numero ? `${clinica.numero} ${clinica.endereco}` : clinica.endereco;
                    params.append('street', street);
                }
                if (clinica.cidade) params.append('city', clinica.cidade);
                if (clinica.uf) params.append('state', clinica.uf);
                if (clinica.cep) params.append('postalcode', clinica.cep);
                params.append('country', 'Brasil');
                params.append('format', 'json');
                params.append('limit', '1');

                const queryURL = `https://nominatim.openstreetmap.org/search?${params.toString()}`;
                const nomRes = await fetch(queryURL, { headers: { 'Accept-Language': 'pt-BR', 'User-Agent': 'SSTExamesFront/1.0' } });
                const geoData = await nomRes.json().catch(() => null);
                let updatePayload = {};
                if (geoData && geoData.length > 0) {
                   updatePayload = { lat: parseFloat(geoData[0].lat), lng: parseFloat(geoData[0].lon) };
                }
                await ClinicaService.updateGeolocalizacao(clinica.id, updatePayload);
             } catch (err) { }
          });
        }

        // Disparar evento do Clarity caso a rota amigável resulte em 0 clínicas
        if (clinicasFormatted.length === 0 && (selectedUf || selectedCidade || selectedBairro)) {
          import('../../../lib/clarity').then(({ trackClarityEvent, setClarityTag }) => {
            setClarityTag('search_estado', selectedUf)
            if (selectedCidade) setClarityTag('search_cidade', selectedCidade)
            if (selectedBairro) setClarityTag('search_bairro', selectedBairro)
            trackClarityEvent('clinica_search_empty_result')
          })
        }
      } catch (error) {
        console.error('Erro ao carregar clínicas:', error)
        // Fallback para dados mockados se API falhar
        setClinicas([])
        setFilteredClinicas([])
      } finally {
        setLoading(false)
      }
    }

    loadClinicas()
  }, [selectedUf, selectedCidade, selectedBairro, initialSlug, processedInitials])

  const handleCepSearch = async () => {
    if (!searchCep) return

    setLoading(true)
    try {
      const cepLimpo = cleanCEP(searchCep)
      
      // Busca dados exatos do CEP pelo ViaCEP primeiro
      const viaCepRes = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`)
      const viaCepData = await viaCepRes.json()
      
      if (viaCepData && !viaCepData.erro) {
         setCepInfo({
            cep: formatCEP(searchCep),
            logradouro: viaCepData.logradouro,
            bairro: viaCepData.bairro,
            cidade: viaCepData.localidade,
            uf: viaCepData.uf
         })
         
         const addressQuery = `${viaCepData.logradouro || ''}, ${viaCepData.bairro || ''}, ${viaCepData.localidade || ''}, ${viaCepData.uf || ''}, Brasil`
         const coords = await fetchNominatimCoordinates(addressQuery)
         
         if (coords) {
            const clinicasData = await ClinicaService.getClinicasFiltradas({
              lat: coords.lat,
              lng: coords.lng,
              estado: viaCepData.uf
            });
            
            const clinicasFormatted = clinicasData.map((clinica: any) => ({
              id: clinica.id,
              nome: clinica.nome,
              bairro: clinica.bairro || '',
              cidade: clinica.cidade,
              uf: clinica.uf,
              cep: clinica.cep,
              endereco: clinica.endereco,
              numero: clinica.numero,
              latitude: clinica.latitude,
              longitude: clinica.longitude,
              geolocalizacao_atualizada_em: clinica.geolocalizacao_atualizada_em,
              distanciaKm: clinica.distanciaKm,
              horarios: clinica.horarios || []
            }))
            
            setFilteredClinicas(clinicasFormatted)

            // Background Job Client-Side
            const dehydrated = clinicasFormatted.filter((c: any) => !c.geolocalizacao_atualizada_em);
            if (dehydrated.length > 0) {
              dehydrated.forEach(async (clinica: any, i: number) => {
                 try {
                    await new Promise(r => setTimeout(r, i * 1500));
                    const params = new URLSearchParams();
                    if (clinica.endereco) {
                        const street = clinica.numero ? `${clinica.numero} ${clinica.endereco}` : clinica.endereco;
                        params.append('street', street);
                    }
                    if (clinica.cidade) params.append('city', clinica.cidade);
                    if (clinica.uf) params.append('state', clinica.uf);
                    if (clinica.cep) params.append('postalcode', clinica.cep);
                    params.append('country', 'Brasil');
                    params.append('format', 'json');
                    params.append('limit', '1');

                    const queryURL = `https://nominatim.openstreetmap.org/search?${params.toString()}`;
                    const nomRes = await fetch(queryURL, { headers: { 'Accept-Language': 'pt-BR', 'User-Agent': 'SSTExamesFront/1.0' } });
                    const geoData = await nomRes.json().catch(() => null);
                    let updatePayload = {};
                    if (geoData && geoData.length > 0) {
                       updatePayload = { lat: parseFloat(geoData[0].lat), lng: parseFloat(geoData[0].lon) };
                    }
                    await ClinicaService.updateGeolocalizacao(clinica.id, updatePayload);
                 } catch (err) { }
              });
            }
            
            if (clinicasFormatted.length > 0) {
               setNearbyMessage(`Encontramos estas opções ordenadas pela proximidade real com o seu CEP.`)
            } else {
               import('../../../lib/clarity').then(({ trackClarityEvent, setClarityTag }) => {
                 setClarityTag('search_cep', cepLimpo)
                 setClarityTag('search_estado', viaCepData.uf)
                 setClarityTag('search_cidade', viaCepData.localidade)
                 trackClarityEvent('clinica_search_cep_empty_result')
               })
               setNearbyMessage(`Nenhuma clínica encontrada no seu estado (${viaCepData.uf}).`)
            }
         } else {
            setNearbyMessage(`Não conseguimos buscar as coordenadas para o endereço do CEP.`)
            setFilteredClinicas([])
         }
      } else {
         setCepInfo({
           cep: formatCEP(searchCep),
           logradouro: 'CEP não encontrado',
           bairro: 'CEP não identificado',
           cidade: 'Desconhecido',
           uf: ''
         })
         setNearbyMessage('Infelizmente, não encontramos os dados deste CEP.')
         setFilteredClinicas([])
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUfFilter = (uf: string) => {
    setSelectedUf(uf)
    setSelectedCidade('')
    setSelectedBairro('')
    setCepInfo(null)
    setSearchCep('')
    setNearbyMessage('')
  }

  const handleCidadeFilter = (cidade: string) => {
    setSelectedCidade(cidade)
    setSelectedBairro('')
    setCepInfo(null)
    setSearchCep('')
    setNearbyMessage('')
  }
  
  const handleBairroFilter = (bairro: string) => {
    setSelectedBairro(bairro)
    setCepInfo(null)
    setSearchCep('')
    setNearbyMessage('')
  }

  const handleVerHorarios = (clinica: ClinicaItem) => {
    setClinicaHorarios(clinica)
  }

  const handleFecharHorarios = () => {
    setClinicaHorarios(null)
  }

  // Estados mockados como fallback
  const estadosMock: EstadoItem[] = [
    { uf: 'AC', nome: 'Acre' },
    { uf: 'AL', nome: 'Alagoas' },
    { uf: 'AP', nome: 'Amapá' },
    { uf: 'AM', nome: 'Amazonas' },
    { uf: 'BA', nome: 'Bahia' },
    { uf: 'CE', nome: 'Ceará' },
    { uf: 'DF', nome: 'Distrito Federal' },
    { uf: 'ES', nome: 'Espírito Santo' },
    { uf: 'GO', nome: 'Goiás' },
    { uf: 'MA', nome: 'Maranhão' },
    { uf: 'MT', nome: 'Mato Grosso' },
    { uf: 'MS', nome: 'Mato Grosso do Sul' },
    { uf: 'MG', nome: 'Minas Gerais' },
    { uf: 'PA', nome: 'Pará' },
    { uf: 'PB', nome: 'Paraíba' },
    { uf: 'PR', nome: 'Paraná' },
    { uf: 'PE', nome: 'Pernambuco' },
    { uf: 'PI', nome: 'Piauí' },
    { uf: 'RJ', nome: 'Rio de Janeiro' },
    { uf: 'RN', nome: 'Rio Grande do Norte' },
    { uf: 'RS', nome: 'Rio Grande do Sul' },
    { uf: 'RO', nome: 'Rondônia' },
    { uf: 'RR', nome: 'Roraima' },
    { uf: 'SC', nome: 'Santa Catarina' },
    { uf: 'SP', nome: 'São Paulo' },
    { uf: 'SE', nome: 'Sergipe' },
    { uf: 'TO', nome: 'Tocantins' }
  ]

  const handleLocationDecision = async (coords: { lat: number; lng: number } | null) => {
    setShowLocationModal(false)
    setHasPromptedLocation(true)
    
    setLoading(true)
    try {
      let data: any[] = []
      if (coords) {
        data = await ClinicaService.getClinicasFiltradas({ lat: coords.lat, lng: coords.lng })
        if (data && data.length > 0) {
           setNearbyMessage('Exibindo clínicas mais próximas da sua localização atual.')
        }
      }
      
      if (!data || data.length === 0) {
        data = await ClinicaService.getClinicas()
      }

      const formatted = data.map((clinica: any) => ({
          id: clinica.id,
          nome: clinica.nome,
          bairro: clinica.bairro || '',
          cidade: clinica.cidade,
          uf: clinica.uf,
          distanciaKm: clinica.distanciaKm,
          horarios: clinica.horarios || []
      }))
      setClinicas(formatted)
      setFilteredClinicas(formatted)
    } catch(err) {
      console.error('Erro ao processar modal de localização:', err)
      setClinicas([])
      setFilteredClinicas([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <LocationPromptModal 
        isOpen={showLocationModal} 
        onClose={() => setShowLocationModal(false)}
        onAllow={handleLocationDecision}
      />
      <CompanyFormModal
        isOpen={showCompanyFormModal}
        onClose={() => setShowCompanyFormModal(false)}
        onSuccess={() => {
           window.location.href = '/agendamento';
        }}
      />
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Clínicas Referenciadas
            </h1>
            <p className="text-lg text-gray-600">
              Encontre a clínica mais próxima de você. Temos mais de 450 unidades em todo o Brasil.
            </p>
          </div>

          {/* Filtros em blocos separados */}
          <div className="mb-10 space-y-6">
            
            {/* BLOCO 1: CEP */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-green-500 p-6 sm:p-8 relative">
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-green-50 p-3 rounded-lg text-green-600 flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Busca Exata por CEP</h3>
                  <p className="text-sm text-gray-500 mt-1">Encontre a clínica mais próxima digitando o CEP de sua residência ou trabalho.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-0 sm:pl-16">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Qual o seu CEP?
                  </label>
                  <input
                    type="text"
                    value={searchCep}
                    onChange={(e) => {
                      const value = e.target.value
                      const formatted = formatCEP(value)
                      setSearchCep(formatted)
                    }}
                    placeholder="Ex: 01001-000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 focus:bg-white text-gray-900 transition-colors"
                    maxLength={9}
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={handleCepSearch}
                    disabled={loading || !searchCep}
                    className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all font-semibold h-[50px] shadow-sm disabled:opacity-50 flex justify-center items-center gap-2"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Buscando...
                      </span>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        Buscar CEP
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* DIVIDER: OU */}
            <div className="relative flex py-4 items-center justify-center">
                <div className="w-1/3 border-t-2 border-dashed border-gray-300"></div>
                <span className="mx-6 bg-gray-100 text-gray-500 font-bold px-4 py-1.5 rounded-full text-sm tracking-widest border border-gray-200 shadow-sm">
                  OU EXPLORE POR LOCALIDADE
                </span>
                <div className="w-1/3 border-t-2 border-dashed border-gray-300"></div>
            </div>

            {/* BLOCO 2: LOCALIDADE */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-green-500 p-6 sm:p-8 relative">
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-green-50 p-3 rounded-lg text-green-600 flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Busca Detalhada por Região</h3>
                  <p className="text-sm text-gray-500 mt-1">Selecione progressivamente o Estado, Cidade e Bairro para visualizar as clínicas disponíveis.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pl-0 sm:pl-16">
                {/* Filtro por Estado */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Estado
                  </label>
                  <SearchableSelect
                    options={[{ uf: '', nome: 'Nenhum' }, ...estados]}
                    value={selectedUf}
                    onChange={handleUfFilter}
                    getOptionValue={(estado: any) => estado.uf}
                    getOptionLabel={(estado: any) => estado.uf ? `${estado.uf} - ${estado.nome}` : estado.nome}
                    placeholder="Selecione o Estado"
                    disabled={false}
                    loading={false}
                    emptyMessage="Nenhum estado"
                  />
                </div>

                {/* Filtro por Cidade */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Cidade
                  </label>
                  <SearchableSelect
                    options={cidades}
                    value={selectedCidade}
                    onChange={handleCidadeFilter}
                    getOptionValue={(cidade: any) => cidade.cidade}
                    getOptionLabel={(cidade: any) => cidade.cidade}
                    placeholder="Selecione a Cidade"
                    disabled={!selectedUf || loadingCidades}
                    loading={loadingCidades}
                    emptyMessage="Nenhuma cidade encontrada"
                    allowClear={false}
                  />
                </div>

                {/* Filtro por Bairro */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Bairro
                  </label>
                  <SearchableSelect
                    options={bairros}
                    value={selectedBairro}
                    onChange={handleBairroFilter}
                    getOptionValue={(bairro: any) => bairro.nome || bairro.bairro}
                    getOptionLabel={(bairro: any) => bairro.nome || bairro.bairro}
                    placeholder="Selecione o Bairro"
                    disabled={!selectedCidade || loadingBairros}
                    loading={loadingBairros}
                    emptyMessage="Nenhum bairro aberto"
                    allowClear={false}
                  />
                </div>
              </div>
            </div>
          </div>

            {/* Resultado da busca por CEP */}
            {cepInfo && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">
                  <strong>Localização encontrada:</strong> {cepInfo.logradouro}, {cepInfo.bairro} - {cepInfo.cidade}/{cepInfo.uf}
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Clínicas ordenadas por proximidade
                </p>
              </div>
            )}

          {/* Alerta de fallback */}
          {nearbyMessage && filteredClinicas.length > 0 && (
            <div className="mb-6 bg-amber-50 rounded-xl shadow-sm border border-amber-200 p-4 flex gap-4 items-center">
               <div className="bg-amber-100 p-2 rounded-lg text-amber-600 flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
               </div>
               <div>
                 <p className="text-amber-800 font-medium">
                   {nearbyMessage}
                 </p>
               </div>
            </div>
          )}

          {/* Lista de clínicas */}
          <div className="space-y-6">
            {loading ? (
              // SKELETON LOADER
              [1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-lg shadow-sm p-6 animate-pulse">
                  <div className="flex justify-between items-start mb-4">
                    <div className="space-y-3 w-2/3">
                      <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                    <div className="w-16 h-6 bg-gray-200 rounded"></div>
                  </div>
                  <div className="mb-4 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <div className="space-y-2 w-1/3">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      </div>
                      <div className="w-32 h-10 bg-gray-200 rounded-lg"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : filteredClinicas.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <p className="text-gray-500">Nenhuma clínica encontrada para os filtros selecionados.</p>
              </div>
            ) : (
              filteredClinicas.map((clinica) => (
                <div key={clinica.id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {clinica.nome}
                      </h3>
                      {('distanciaKm' in clinica && clinica.distanciaKm !== undefined) ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          A {(clinica as any).distanciaKm} km de distância
                        </span>
                      ) : null}
                    </div>
                    <div className="text-right">
                      <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded">
                        {clinica.uf}
                      </span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-gray-700">
                      <strong>Localização:</strong><br />
                      {clinica.bairro} - {clinica.cidade}/{clinica.uf}
                    </p>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-gray-700">
                          <strong>Horário de funcionamento:</strong><br />
                          {clinica.horarios && clinica.horarios.length > 0
                            ? `${clinica.horarios.length} dia(s) configurado(s)`
                            : 'Horário não informado'
                          }
                        </p>
                      </div>
                      {clinica.horarios && clinica.horarios.length > 0 && (
                        <button
                          onClick={() => handleVerHorarios(clinica)}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                          Ver Horários
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end">
                    <button
                      onClick={() => {
                        localStorage.setItem('preSelectedClinica', String(clinica.id));
                        localStorage.setItem('preSelectedClinicaNome', clinica.nome);
                        setShowCompanyFormModal(true);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 group w-full sm:w-auto"
                    >
                      <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Agendar nesta Clínica
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <div className="bg-green-50 border border-green-200 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-green-900 mb-4">
                Pronto para agendar?
              </h3>
              <p className="text-green-700 mb-6">
                Escolha uma clínica e comece seu agendamento agora mesmo.
              </p>
              <a
                href="/"
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                Agendar Exames
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Horários */}
      {clinicaHorarios && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative p-5 border w-full max-w-[95vw] shadow-lg rounded-lg bg-white max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                Horários de Funcionamento - {clinicaHorarios.nome}
              </h3>
              <button
                onClick={handleFecharHorarios}
                className="text-gray-400 hover:text-gray-600 p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-2">
              <HorariosClinica horarios={clinicaHorarios.horarios || []} />
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={handleFecharHorarios}
                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  )
}

